import React, { useState, useCallback } from "react";
import { Popover, message } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
  SmileOutlined,
  CodeOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { MessageSquareQuote, Code2 } from "lucide-react";
import EmojiPicker, { Theme } from "emoji-picker-react";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";

export type FormatType =
  | "bold"
  | "italic"
  | "strikethrough"
  | "underline"
  | "monospace"
  | "inline-code"
  | "ordered-list"
  | "unordered-list"
  | "blockquote";

export interface FormatButton {
  type: FormatType;
  icon: React.ReactNode;
  title: string;
  disabled?: boolean;
  unsupported?: boolean;
  unsupportedMessage?: string;
}

export interface FormattingToolbarProps {
  onFormat: (format: FormatType) => void;
  onEmojiSelect?: (emoji: string) => void;
  showEmojiPicker?: boolean;
  onEmojiPickerChange?: (open: boolean) => void;
  characterCount?: number;
  maxCharacters?: number;
  showCharacterCount?: boolean;
  showEmojiButton?: boolean;
  formatButtons?: FormatButton[];
  disabledFormats?: FormatType[];
  hiddenFormats?: FormatType[];
  size?: "small" | "medium" | "large";
  className?: string;
  emojiTheme?: Theme;
  emojiPickerWidth?: number;
  emojiPickerHeight?: number;
}

const defaultFormatButtons: FormatButton[] = [
  { type: "bold", icon: <BoldOutlined />, title: "Bold (*text*)" },
  { type: "italic", icon: <ItalicOutlined />, title: "Italic (_text_)" },
  {
    type: "strikethrough",
    icon: <StrikethroughOutlined />,
    title: "Strikethrough (~text~)",
  },
  {
    type: "monospace",
    icon: <CodeOutlined />,
    title: "Monospace (```text```)",
  },
  {
    type: "inline-code",
    icon: <Code2 className="w-4 h-4" />,
    title: "Inline Code (`text`)",
  },
  {
    type: "unordered-list",
    icon: <UnorderedListOutlined />,
    title: "Bulleted List (- text)",
  },
  {
    type: "ordered-list",
    icon: <OrderedListOutlined />,
    title: "Numbered List (1. text)",
  },
  {
    type: "blockquote",
    icon: <MessageSquareQuote className="w-4 h-4" />,
    title: "Block Quote (> text)",
  },
  {
    type: "underline",
    icon: <UnderlineOutlined />,
    title: "Underline (not supported by WhatsApp)",
    unsupported: true,
    unsupportedMessage: "WhatsApp doesn't support underline formatting",
  },
];

const sizeConfig = {
  small: {
    button: "w-7 h-7 text-xs",
    icon: "text-sm",
    padding: "px-2 py-1",
    text: "text-xs",
  },
  medium: {
    button: "w-8 h-8 text-sm",
    icon: "text-base",
    padding: "px-3 py-2",
    text: "text-sm",
  },
  large: {
    button: "w-9 h-9 text-base",
    icon: "text-lg",
    padding: "px-4 py-2.5",
    text: "text-base",
  },
} as const;

export const FormattingToolbar: React.FC<FormattingToolbarProps> = ({
  onFormat,
  onEmojiSelect,
  showEmojiPicker = false,
  onEmojiPickerChange,
  characterCount = 0,
  maxCharacters = 1024,
  showCharacterCount = true,
  showEmojiButton = true,
  formatButtons = defaultFormatButtons,
  disabledFormats = [],
  hiddenFormats = [],
  size = "medium",
  className = "",
  emojiTheme = Theme.LIGHT,
  emojiPickerWidth = 320,
  emojiPickerHeight = 400,
}) => {
  const [hoveredButton, setHoveredButton] = useState<
    FormatType | "emoji" | null
  >(null);

  const handleFormatClick = useCallback(
    (button: FormatButton) => {
      if (button.unsupported && button.unsupportedMessage) {
        message.info(button.unsupportedMessage);
        return;
      }
      onFormat(button.type);
    },
    [onFormat],
  );

  const handleEmojiClick = useCallback(
    (emojiData: { emoji: string }) => {
      onEmojiSelect?.(emojiData.emoji);
    },
    [onEmojiSelect],
  );

  const isDisabled = (type: FormatType) => disabledFormats.includes(type);

  const getCharacterCountColor = () => {
    if (!maxCharacters) return colors.textMuted;
    const ratio = characterCount / maxCharacters;
    if (ratio >= 1) return colors.error;
    if (ratio >= 0.9) return colors.warning;
    return colors.textMuted;
  };

  const config = sizeConfig[size];

  return (
    <div
      className={cn(
        "flex items-center justify-between border-t",
        config.padding,
        className,
      )}
      style={{
        backgroundColor: colors.surfaceLighter,
        borderColor: colors.surfaceSubtle,
      }}
    >
      <div className="flex items-center gap-0.5">
        {formatButtons
          .filter((button) => !hiddenFormats.includes(button.type))
          .map((button) => {
            const disabled = isDisabled(button.type) || button.disabled;
            const isHovered = hoveredButton === button.type;

            return (
              <button
                key={button.type}
                type="button"
                onClick={() => handleFormatClick(button)}
                onMouseEnter={() => setHoveredButton(button.type)}
                onMouseLeave={() => setHoveredButton(null)}
                disabled={disabled}
                title={button.title}
                className={cn(
                  "flex items-center justify-center rounded transition-all duration-150",
                  config.button,
                  disabled && "opacity-40 cursor-not-allowed",
                  !disabled && "cursor-pointer",
                )}
                style={{
                  backgroundColor:
                    isHovered && !disabled
                      ? colors.surfaceSubtle
                      : "transparent",
                  color:
                    isHovered && !disabled
                      ? colors.textDefault
                      : colors.textMuted,
                }}
              >
                <span className={config.icon}>{button.icon}</span>
              </button>
            );
          })}

        {showEmojiButton && onEmojiSelect && (
          <Popover
            content={
              <EmojiPicker
                onEmojiClick={handleEmojiClick}
                width={emojiPickerWidth}
                height={emojiPickerHeight}
                theme={emojiTheme}
                lazyLoadEmojis
                searchPlaceholder="Search emoji..."
              />
            }
            trigger="click"
            open={showEmojiPicker}
            onOpenChange={onEmojiPickerChange}
            placement="topLeft"
            arrow={false}
          >
            <button
              type="button"
              onMouseEnter={() => setHoveredButton("emoji")}
              onMouseLeave={() => setHoveredButton(null)}
              title="Add Emoji"
              className={cn(
                "flex items-center justify-center rounded transition-all duration-150 cursor-pointer",
                config.button,
              )}
              style={{
                backgroundColor:
                  hoveredButton === "emoji"
                    ? colors.surfaceSubtle
                    : "transparent",
                color:
                  hoveredButton === "emoji"
                    ? colors.textDefault
                    : colors.textMuted,
              }}
            >
              <span className={config.icon}>
                <SmileOutlined />
              </span>
            </button>
          </Popover>
        )}
      </div>

      {showCharacterCount && (
        <span
          className={cn("tabular-nums select-none", config.text)}
          style={{ color: getCharacterCountColor() }}
        >
          {characterCount}/{maxCharacters}
        </span>
      )}
    </div>
  );
};

export default FormattingToolbar;
